import { ResourceTypeEnum } from '@/shared/components/ui/resource-icon/resource-icon.types';
import { TPolicyInputSchema } from '@/shared/features/rbac/components/create-policy/policy-editor/policy-editor.types';
import { TPolicy } from '@/shared/features/rbac/components/policies/policies.types';
import { WILD_CARD } from '@/shared/features/rbac/hooks/use-wild-card-option/use-wild-card-option';

/**
 * Resolve the resource type for a policy from its action/resource. Looks the
 * action up in the configured policies first — Backoffice namespaces actions
 * per resource (`backofficeUser:`, `backofficeTenant:`, …) whose prefix doesn't
 * map onto a `ResourceTypeEnum` value, so the config lookup is what identifies
 * the resource — then falls back to the action/resource prefix for actions not
 * present in the config (e.g. hand-typed JSON).
 */
export const getResourceTypeFromActions = (
  configuredPolicies: TPolicy,
  defaultResourceType: ResourceTypeEnum,
  action?: string,
  resource?: string,
): ResourceTypeEnum => {
  const matchedEntry = action
    ? Object.entries(configuredPolicies).find(([, actions]) =>
        actions.includes(action),
      )
    : undefined;

  if (matchedEntry) {
    return matchedEntry[0] as ResourceTypeEnum;
  }

  const resourceTypes = [action?.split(':')[0], resource?.split(':')[0]];
  const validType = resourceTypes.find((type) => type && type !== '*');

  if (validType?.toLowerCase() === 'resourcegroup') {
    return ResourceTypeEnum.Group;
  }

  if (validType?.toLowerCase() === 'auditlog') {
    return ResourceTypeEnum.AuditLog;
  }

  return (validType?.toUpperCase() as ResourceTypeEnum) || defaultResourceType;
};

/**
 * Whether the JSON policy editor has no usable content — empty/whitespace text
 * or an empty array. Invalid-but-non-empty JSON counts as having content so the
 * user can still copy/download what they typed to fix it elsewhere.
 */
export const isPolicyJsonEmpty = (jsonPolicy?: string): boolean => {
  if (!jsonPolicy?.trim()) {
    return true;
  }

  try {
    const parsed = JSON.parse(jsonPolicy);
    return Array.isArray(parsed) && parsed.length === 0;
  } catch {
    return false;
  }
};

/**
 * Resource strings encode their aggregation: groups contain `:group:` (or the
 * `resourceGroup:` prefix), tags contain `:tag:` (or the `tag:` prefix), and
 * everything else (`<type>:id:<id>`, `<type>:*`) is a direct id resource. The
 * id/group/tag selectors all write to the same `resource` field, so these
 * classifiers let each one display only its own selections.
 */
export const isGroupResource = (resource: string): boolean =>
  resource.includes(':group:') || resource.startsWith('resourceGroup:');

export const isTagResource = (resource: string): boolean =>
  resource.includes(':tag:') || resource.startsWith('tag:');

export const isIdResource = (resource: string): boolean =>
  !isGroupResource(resource) && !isTagResource(resource);

/**
 * Removes the UI-only fields (`resourceType`, `groupedByRoleGroup`) from each
 * policy so the value sent to the JSON editor and the backend only contains
 * real policy fields.
 */
export const stripPolicyUiFields = <
  T extends { resourceType?: unknown; groupedByRoleGroup?: unknown },
>(
  policies: T[],
): Omit<T, 'resourceType' | 'groupedByRoleGroup'>[] =>
  policies.map(
    ({
      resourceType: _resourceType,
      groupedByRoleGroup: _groupedByRoleGroup,
      ...policy
    }) => policy,
  );

const getResourcePrefix = (value: string) => value.split(':')[0];

/**
 * The policy prefix for a resource type. Console enums are SCREAMING_CASE and
 * lowercase to the prefix; Backoffice enums are already the camelCase prefix.
 */
export const getResourceTypePrefix = (resourceType: string) =>
  resourceType === resourceType.toUpperCase()
    ? resourceType.toLowerCase()
    : resourceType;

/**
 * Resource value that scopes a policy to everything in a group, mirroring the
 * value produced by the group aggregation selector (`<prefix>:group:<groupId>`,
 * with `resourceGroup`/`auditLog` special-cased).
 */
export const getGroupAggregationResource = (
  resourceType: ResourceTypeEnum,
  groupId: string,
): string => {
  const prefix = getResourceTypePrefix(resourceType);

  if (prefix === ResourceTypeEnum.Group.toLowerCase()) {
    return `resourceGroup:group:${groupId}`;
  }

  if (prefix === ResourceTypeEnum.AuditLog.toLowerCase()) {
    return `auditLog:group:${groupId}`;
  }

  return `${prefix}:group:${groupId}`;
};

/**
 * Whether a policy can be edited in the visual editor. The editor represents a
 * single resource type per policy, so a policy is editable only when all of its
 * actions and resources share one resource-type prefix. Policies created in the
 * new flow (with a `resourceType`) and still-empty policies are always editable;
 * legacy policies that mix resource types (still valid for the backend) are not.
 */
export const isPolicyEditable = (policy?: TPolicyInputSchema): boolean => {
  if (policy?.resourceType) {
    return true;
  }

  const values = [...(policy?.action ?? []), ...(policy?.resource ?? [])];

  if (!values.length) {
    return true;
  }

  if (values.some((value) => value === WILD_CARD)) {
    return false;
  }

  const prefixes = new Set(values.map(getResourcePrefix).filter(Boolean));

  return prefixes.size === 1;
};

/**
 * Splits a legacy policy into one policy per resource type: actions are grouped
 * by their resource-type prefix and each group keeps the resources that share
 * that prefix. A bare `*` resource ("all resources") is scoped to each split as
 * `<prefix>:*`, preserving the intent while keeping the policy valid. Bare `*`
 * actions are dropped, and groups left with no resource of their own (an action
 * prefix the original resources never matched) are skipped rather than emitted
 * as invalid resourceless policies.
 */
export const convertPolicyToNewFormat = (
  policy: TPolicyInputSchema,
  configuredPolicies: TPolicy,
  defaultResourceType: ResourceTypeEnum,
): TPolicyInputSchema[] => {
  const actionsByPrefix = (policy.action ?? []).reduce<
    Record<string, string[]>
  >((groups, action) => {
    const prefix = getResourcePrefix(action);

    if (!prefix || prefix === WILD_CARD) {
      return groups;
    }

    groups[prefix] = [...(groups[prefix] ?? []), action];
    return groups;
  }, {});

  const hasWildCardResource = (policy.resource ?? []).includes(WILD_CARD);

  return Object.entries(actionsByPrefix).flatMap(([prefix, actions]) => {
    const resource = (policy.resource ?? []).filter(
      (value) => getResourcePrefix(value) === prefix,
    );

    const wildCardResource = `${prefix}:${WILD_CARD}`;

    if (hasWildCardResource && !resource.includes(wildCardResource)) {
      resource.push(wildCardResource);
    }

    if (!resource.length) {
      return [];
    }

    return [
      {
        name: policy.name,
        description: policy.description,
        action: actions,
        resource,
        resourceType: getResourceTypeFromActions(
          configuredPolicies,
          defaultResourceType,
          actions[0],
          resource[0],
        ),
      },
    ];
  });
};

/** Whether converting a legacy policy omitted at least one original action. */
export const hasDroppedActionsDuringConversion = (
  policy: TPolicyInputSchema,
  convertedPolicies: TPolicyInputSchema[],
): boolean => {
  const convertedActions = new Set(
    convertedPolicies.flatMap((convertedPolicy) => convertedPolicy.action),
  );

  return (policy.action ?? []).some((action) => !convertedActions.has(action));
};

export const getObjectIndexAtPosition = (
  jsonContent: string,
  line: number,
  column: number,
): number | null => {
  try {
    const parsedContent = JSON.parse(jsonContent);

    if (!Array.isArray(parsedContent)) {
      console.log('Content is not an array');
      return null;
    }

    // Convert line/column to character offset
    const lines = jsonContent.split('\n');
    let charOffset = 0;

    // Calculate character offset up to the clicked line
    for (let i = 0; i < Math.min(line - 1, lines.length); i++) {
      charOffset += lines[i].length + 1; // +1 for newline character
    }
    // Add column offset within the current line
    charOffset += Math.min(column - 1, lines[line - 1]?.length || 0);

    // Parse through JSON to find object boundaries
    let currentObjectIndex = -1;
    let inMainArray = false;
    let braceDepth = 0;
    let bracketDepth = 0;
    let inString = false;
    let escapeNext = false;
    let objectStartPositions: number[] = [];
    let objectEndPositions: number[] = [];

    for (let i = 0; i < jsonContent.length; i++) {
      const char = jsonContent[i];

      // Handle string escaping
      if (escapeNext) {
        escapeNext = false;
        continue;
      }

      if (char === '\\' && inString) {
        escapeNext = true;
        continue;
      }

      // Handle string boundaries
      if (char === '"' && !escapeNext) {
        inString = !inString;
        continue;
      }

      // Skip characters inside strings
      if (inString) continue;

      // Handle array brackets
      if (char === '[') {
        bracketDepth++;
        if (bracketDepth === 1) {
          inMainArray = true;
        }
        continue;
      }

      if (char === ']') {
        bracketDepth--;
        if (bracketDepth === 0) {
          inMainArray = false;
        }
        continue;
      }

      // Only process if we're in the main array
      if (!inMainArray) continue;

      // Handle object braces
      if (char === '{') {
        if (braceDepth === 0) {
          // Starting a new top-level object
          currentObjectIndex++;
          objectStartPositions[currentObjectIndex] = i;
        }
        braceDepth++;
      }

      if (char === '}') {
        braceDepth--;
        if (braceDepth === 0) {
          // Ending a top-level object
          objectEndPositions[currentObjectIndex] = i;
        }
      }
    }

    // Find which object contains the clicked position
    for (let i = 0; i < objectStartPositions.length; i++) {
      const startPos = objectStartPositions[i];
      const endPos = objectEndPositions[i];

      if (startPos !== undefined && endPos !== undefined) {
        if (charOffset >= startPos && charOffset <= endPos) {
          return i;
        }
      }
    }

    // If we didn't find an exact match, find the closest object
    // This handles cases where the user clicks on whitespace or commas between objects
    let closestObjectIndex = -1;
    let minDistance = Infinity;

    for (let i = 0; i < objectStartPositions.length; i++) {
      const startPos = objectStartPositions[i];
      const endPos = objectEndPositions[i];

      if (startPos !== undefined && endPos !== undefined) {
        let distance;

        if (charOffset < startPos) {
          distance = startPos - charOffset;
        } else if (charOffset > endPos) {
          distance = charOffset - endPos;
        } else {
          // This shouldn't happen as we already checked exact matches above
          distance = 0;
        }

        if (distance < minDistance) {
          minDistance = distance;
          closestObjectIndex = i;
        }
      }
    }

    // Only return closest match if it's reasonably close (within same line or adjacent lines)
    if (closestObjectIndex !== -1 && minDistance < 100) {
      return closestObjectIndex;
    }

    return null;
  } catch (error) {
    console.error('Error parsing JSON:', error);
    return null;
  }
};
